import { api } from '@invisoutlet/shared/api';
import type { AlertDisplay } from '@invisoutlet/shared/components';
import { useAuthContext } from '@invisoutlet/shared/context';
import type { Alert, Entity } from '@invisoutlet/shared/types';
import { formatRelativeTime } from '@invisoutlet/shared/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { EnterpriseLayout } from '../components/enterprise-layout.js';

// ─── Display helpers ──────────────────────────────────────────────────────────

const SEVERITY_BADGE: Record<Alert['severity'], string> = {
  critical: 'border border-danger/30 bg-danger/10 text-danger',
  warning: 'border border-warning/30 bg-warning/10 text-warning',
  info: 'border border-info/30 bg-info/10 text-info',
};

const STATUS_BADGE: Record<Alert['status'], string> = {
  active: 'bg-danger/10 text-danger',
  acknowledged: 'bg-warning/10 text-warning',
  resolved: 'bg-bg-elevated text-text-secondary',
};

/** Convert a raw Alert + entity context into an AlertDisplay for the banner bar. */
function toAlertDisplay(alert: Alert, entity: Entity | undefined): AlertDisplay {
  const building = entity?.buildings.find((b) => b.id === alert.buildingId);
  return {
    id: alert.id,
    severity: alert.severity,
    message: alert.message,
    deviceName: alert.deviceId, // resolved to real name once device lookup is added
    buildingName: building?.name ?? alert.buildingId,
    room: '',
    createdAt: alert.createdAt,
    status: alert.status,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function AlertsSkeleton() {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-bg-primary">
      <div className="divide-y divide-border">
        {[1, 2, 3, 4, 5].map((n) => (
          <div key={n} className="flex items-center gap-4 px-4 py-3">
            <div className="h-5 w-16 animate-pulse rounded-full bg-bg-elevated" />
            <div className="h-4 flex-1 animate-pulse rounded bg-bg-elevated" />
            <div className="h-4 w-24 animate-pulse rounded bg-bg-elevated" />
            <div className="h-5 w-20 animate-pulse rounded-full bg-bg-elevated" />
          </div>
        ))}
      </div>
    </div>
  );
}

function EmptyAlerts() {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-success/10">
        <svg
          className="h-8 w-8 text-success"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      </div>
      <h2 className="mb-2 text-lg font-semibold text-text-primary">All clear</h2>
      <p className="max-w-sm text-sm text-text-secondary">No alerts match your current filters.</p>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AlertsPage() {
  const { user } = useAuthContext();
  const entityId = user!.entityId!;
  const queryClient = useQueryClient();

  const [filterStatus, setFilterStatus] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterBuildingId, setFilterBuildingId] = useState('');

  // Entity (shared cache, used for building names)
  const { data: entityRes } = useQuery({
    queryKey: ['entity', entityId],
    queryFn: () => api.get<Entity>(`/api/entities/${entityId}`),
  });
  const entity = entityRes?.data;
  const buildings = entity?.buildings ?? [];

  // Alerts from real API
  const alertsQuery = useQuery({
    queryKey: ['alerts', entityId, filterStatus, filterSeverity, filterBuildingId],
    queryFn: () => {
      const params = new URLSearchParams({ limit: '100' });
      if (filterStatus) params.set('status', filterStatus);
      if (filterSeverity) params.set('severity', filterSeverity);
      if (filterBuildingId) params.set('buildingId', filterBuildingId);
      return api.get<Alert[]>(`/api/entities/${entityId}/alerts?${params.toString()}`);
    },
    enabled: !!entityId,
  });

  const allAlerts = alertsQuery.data?.data ?? [];

  // Build AlertDisplay list for the banner (active only)
  const bannerAlerts: AlertDisplay[] = allAlerts
    .filter((a) => a.status === 'active')
    .map((a) => toAlertDisplay(a, entity));

  // Acknowledge mutation
  const acknowledgeMutation = useMutation({
    mutationFn: (alertId: string) =>
      api.patch(`/api/entities/${entityId}/alerts/${alertId}`, { status: 'acknowledged' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['alerts', entityId] });
    },
  });

  const handleAcknowledge = useCallback(
    (alertId: string) => {
      acknowledgeMutation.mutate(alertId);
    },
    [acknowledgeMutation],
  );

  return (
    <EnterpriseLayout
      alerts={bannerAlerts}
      title="Alerts"
      onAcknowledgeAlert={!user?.readOnly ? handleAcknowledge : undefined}
    >
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl font-bold text-text-primary">Alerts</h1>
          <p className="mt-1 text-sm text-text-secondary">
            Monitor and manage alerts across all your buildings.
          </p>
        </div>

        {/* Filters */}
        <div className="mb-4 flex flex-wrap gap-3">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="rounded-lg border border-border bg-bg-surface px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
            aria-label="Filter by status"
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="resolved">Resolved</option>
          </select>

          <select
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value)}
            className="rounded-lg border border-border bg-bg-surface px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
            aria-label="Filter by severity"
          >
            <option value="">All severities</option>
            <option value="critical">Critical</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>

          {buildings.length > 0 && (
            <select
              value={filterBuildingId}
              onChange={(e) => setFilterBuildingId(e.target.value)}
              className="rounded-lg border border-border bg-bg-surface px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
              aria-label="Filter by building"
            >
              <option value="">All buildings</option>
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          )}

          {(filterStatus || filterSeverity || filterBuildingId) && (
            <button
              type="button"
              onClick={() => {
                setFilterStatus('');
                setFilterSeverity('');
                setFilterBuildingId('');
              }}
              className="rounded-lg px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Table */}
        {alertsQuery.isLoading ? (
          <AlertsSkeleton />
        ) : alertsQuery.isError ? (
          <div className="flex items-center justify-between rounded-xl border border-danger/20 bg-danger/5 px-4 py-3">
            <p className="text-sm text-danger">Failed to load alerts.</p>
            <button
              type="button"
              onClick={() => void alertsQuery.refetch()}
              className="text-sm font-medium text-accent hover:underline"
            >
              Retry
            </button>
          </div>
        ) : allAlerts.length === 0 ? (
          <EmptyAlerts />
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border bg-bg-primary">
            <table className="w-full min-w-[700px] text-sm">
              <thead>
                <tr className="border-b border-border bg-bg-surface text-left">
                  <th className="px-4 py-3 text-xs font-medium uppercase tracking-wide text-text-secondary">
                    Severity
                  </th>
                  <th className="px-4 py-3 text-xs font-medium uppercase tracking-wide text-text-secondary">
                    Message
                  </th>
                  <th className="px-4 py-3 text-xs font-medium uppercase tracking-wide text-text-secondary">
                    Building
                  </th>
                  <th className="px-4 py-3 text-xs font-medium uppercase tracking-wide text-text-secondary">
                    Status
                  </th>
                  <th className="px-4 py-3 text-xs font-medium uppercase tracking-wide text-text-secondary">
                    Triggered
                  </th>
                  {!user?.readOnly && (
                    <th className="px-4 py-3 text-xs font-medium uppercase tracking-wide text-text-secondary">
                      <span className="sr-only">Actions</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {allAlerts.map((alert) => {
                  const buildingName =
                    entity?.buildings.find((b) => b.id === alert.buildingId)?.name ??
                    alert.buildingId;
                  const isResolved = alert.status === 'resolved';

                  return (
                    <tr key={alert.id} className={isResolved ? 'opacity-50' : undefined}>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-xs font-bold uppercase ${SEVERITY_BADGE[alert.severity]}`}
                        >
                          {alert.severity}
                        </span>
                      </td>
                      <td className="max-w-xs px-4 py-3 text-text-primary">{alert.message}</td>
                      <td className="px-4 py-3 text-text-secondary">{buildingName}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_BADGE[alert.status]}`}
                        >
                          {alert.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-text-secondary">
                        {formatRelativeTime(alert.createdAt)}
                      </td>
                      {!user?.readOnly && (
                        <td className="px-4 py-3">
                          {alert.status === 'active' && (
                            <button
                              type="button"
                              onClick={() => handleAcknowledge(alert.id)}
                              disabled={acknowledgeMutation.isPending}
                              className="rounded px-2 py-1 text-xs text-text-secondary ring-1 ring-border hover:bg-bg-elevated hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
                            >
                              Acknowledge
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </EnterpriseLayout>
  );
}
